import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../api/client';

export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [restrictReason, setRestrictReason] = useState('');
  const [showRestrict, setShowRestrict] = useState(false);
  const [commissionForm, setCommissionForm] = useState({
    loadInCommission: '',
    withdrawalCommission: '',
  });
  const [showCommissionModal, setShowCommissionModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers({ search, status: statusFilter });
      setUsers(response.data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleFreeze = async (userId) => {
    if (window.confirm('Are you sure you want to freeze this account?')) {
      try {
        await adminAPI.freezeUser(userId);
        fetchUsers();
      } catch (err) {
        setError('Failed to freeze account');
      }
    }
  };

  const handleActivate = async (userId) => {
    try {
      await adminAPI.activateUser(userId);
      fetchUsers();
    } catch (err) {
      setError('Failed to activate account');
    }
  };

  const handleRestrict = async () => {
    if (!restrictReason.trim()) {
      setError('Restriction reason is required');
      return;
    }
    try {
      await adminAPI.restrictUser(selectedUser.id, { reason: restrictReason });
      setShowRestrict(false);
      setRestrictReason('');
      fetchUsers();
    } catch (err) {
      setError('Failed to restrict account');
    }
  };

  const handleShowCommission = (user) => {
    setSelectedUser(user);
    setCommissionForm({
      loadInCommission: '',
      withdrawalCommission: '',
      referralCommission: '',
    });
    setShowCommissionModal(true);
  };

  const handleUpdateCommission = async () => {
    if (!commissionForm.loadInCommission || !commissionForm.withdrawalCommission || commissionForm.referralCommission === '') {
      setError('Commission rates required');
      return;
    }
    try {
      await adminAPI.updateCommissionSettings({
        userId: selectedUser.id,
        loadInCommission: parseFloat(commissionForm.loadInCommission),
        withdrawalCommission: parseFloat(commissionForm.withdrawalCommission),
        referralCommission: parseFloat(commissionForm.referralCommission),
      });
      setShowCommissionModal(false);
      fetchUsers();
    } catch (err) {
      setError('Failed to update commission settings');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      frozen: 'bg-blue-100 text-blue-800',
      restricted: 'bg-orange-100 text-orange-800',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-600">Manage Users</h1>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-gray-800"
          >
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-8 px-4">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Search & Filter */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <form onSubmit={handleSearchSubmit} className="flex gap-4">
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Search by username or email..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="frozen">Frozen</option>
              <option value="restricted">Restricted</option>
            </select>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
          </form>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {users.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No users found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Username</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Wallet Balance</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Deposit Commission</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                      <td className="px-6 py-4 text-sm">{getStatusBadge(user.account_status)}</td>
                      <td className="px-6 py-4 text-sm text-right font-semibold">₹{parseFloat(user.wallet_balance || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className="text-blue-600 font-semibold">{user.load_in_commission || 20}%</span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {user.is_admin ? (
                          <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                            Admin
                          </span>
                        ) : (
                          <span className="text-gray-500">User</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleShowCommission(user)}
                            className="text-blue-600 hover:underline text-xs"
                            title="Edit commission rates"
                          >
                            Commission
                          </button>
                          {user.account_status === 'active' ? (
                            <>
                              <button
                                onClick={() => handleFreeze(user.id)}
                                className="text-orange-600 hover:underline text-xs"
                                title="Freeze account"
                              >
                                Freeze
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowRestrict(true);
                                }}
                                className="text-red-600 hover:underline text-xs"
                                title="Restrict account"
                              >
                                Restrict
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleActivate(user.id)}
                              className="text-green-600 hover:underline text-xs"
                              title="Activate account"
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Restrict Modal */}
      {showRestrict && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Restrict Account: {selectedUser.username}
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
              <textarea
                value={restrictReason}
                onChange={(e) => setRestrictReason(e.target.value)}
                placeholder="Enter restriction reason..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowRestrict(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRestrict}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Restrict
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commission Modal */}
      {showCommissionModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Commission Settings: {selectedUser.username}
            </h2>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Load In Commission (%)</label>
                <input
                  type="number"
                  value={commissionForm.loadInCommission}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, loadInCommission: e.target.value }))}
                  placeholder="e.g., 2.5"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Withdrawal Commission (%)</label>
                <input
                  type="number"
                  value={commissionForm.withdrawalCommission}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, withdrawalCommission: e.target.value }))}
                  placeholder="e.g., 10"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referral Commission (%)</label>
                <input
                  type="number"
                  value={commissionForm.referralCommission}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, referralCommission: e.target.value }))}
                  placeholder="e.g., 2"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCommissionModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCommission}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
